// src/components/informatie/InformatieOverview.tsx
// Hoofdpagina voor /informatie met alle categorieën
// WFT Study Buddy - Content Management System
// MODERN GLASSMORPHISM DESIGN

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  getCategories,
  getPublishedArticles,
  CategoryInfo,
  Article,
  truncateText
} from '../../services/contentService';

// Category icons
const CategoryIcons: Record<string, React.ReactNode> = {
  examens: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
    </svg>
  ),
  begrippen: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
    </svg>
  ),
  studietips: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
    </svg>
  ),
  statistieken: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
    </svg>
  ),
};

// Category gradient colors for visual distinction
const CategoryGradients: Record<string, string> = {
  examens: 'from-blue-500 to-indigo-600',
  begrippen: 'from-emerald-500 to-teal-600',
  studietips: 'from-amber-500 to-orange-600',
  statistieken: 'from-purple-500 to-pink-600',
};

const InformatieOverview: React.FC = () => {
  const [categories, setCategories] = useState<CategoryInfo[]>([]);
  const [recentArticles, setRecentArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Track scroll for header styling
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [categoriesData, articlesData] = await Promise.all([
        getCategories(),
        getPublishedArticles({ limit: 6 })
      ]);
      setCategories(categoriesData);
      setRecentArticles(articlesData.articles);
    } catch (err: any) {
      setError(err.message || 'Fout bij laden data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>WFT Informatie & Kennisbank | WFT Study Buddy</title>
        <meta 
          name="description" 
          content="Alles over WFT examens: scoringsregels, begrippen, studietips en slagingspercentages. Bereid je optimaal voor op je WFT certificering." 
        />
        <link rel="canonical" href="https://wft-study-buddy.nl/informatie" />
        
        {/* Open Graph */}
        <meta property="og:title" content="WFT Informatie & Kennisbank | WFT Study Buddy" />
        <meta property="og:description" content="Alles over WFT examens: scoringsregels, begrippen, studietips en slagingspercentages." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://wft-study-buddy.nl/informatie" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 relative">
        {/* Decorative Background Blobs */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse" />
          <div className="absolute top-40 right-10 w-72 h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute bottom-40 left-1/3 w-72 h-72 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl opacity-15" />
        </div>

        {/* Main Header */}
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white/90 backdrop-blur-lg shadow-lg py-3' : 'bg-white/60 backdrop-blur-sm py-4'
        }`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center">
              {/* Logo */}
              <Link to="/" className="flex items-center space-x-2 group">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center transform group-hover:scale-105 transition-transform">
                  <span className="text-white font-bold text-xl">W</span>
                </div>
                <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  WFT Study Buddy
                </span>
              </Link>

              {/* Desktop Navigation */}
              <div className="hidden md:flex items-center space-x-6">
                <Link to="/#features" className="text-gray-600 hover:text-blue-600 transition">Features</Link>
                <Link to="/#how-it-works" className="text-gray-600 hover:text-blue-600 transition">Hoe werkt het?</Link>
                <Link to="/#pricing" className="text-gray-600 hover:text-blue-600 transition">Prijzen</Link>
                <Link to="/informatie" className="text-blue-600 font-medium">Informatie</Link>
                <Link to="/login" className="text-gray-600 hover:text-blue-600 transition">Inloggen</Link>
                <Link 
                  to="/register" 
                  className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-5 py-2 rounded-lg hover:shadow-lg transform hover:scale-105 transition-all"
                >
                  Registreren
                </Link>
              </div>

              {/* Mobile Menu Button */}
              <button 
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden text-gray-600 hover:text-blue-600 transition-colors p-2"
                aria-label="Menu"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isMobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden bg-white/95 backdrop-blur-lg border-t border-gray-100 shadow-lg">
              <div className="px-4 py-4 space-y-3">
                <Link to="/" className="block py-2 text-gray-600 hover:text-blue-600">Home</Link>
                <Link to="/#features" className="block py-2 text-gray-600 hover:text-blue-600">Features</Link>
                <Link to="/#pricing" className="block py-2 text-gray-600 hover:text-blue-600">Prijzen</Link>
                <Link to="/informatie" className="block py-2 text-blue-600 font-medium">Informatie</Link>
                <hr className="border-gray-200" />
                <Link to="/login" className="block py-2 text-gray-600 hover:text-blue-600">Inloggen</Link>
                <Link 
                  to="/register" 
                  className="block w-full text-center bg-gradient-to-r from-blue-600 to-purple-600 text-white px-5 py-2.5 rounded-lg"
                >
                  Registreren
                </Link>
              </div>
            </div>
          )}
        </nav>

        {/* Hero Section */}
        <section className="relative z-10 pt-24 sm:pt-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
            <div className="max-w-3xl">
              {/* Breadcrumb */}
              <nav className="flex items-center gap-2 text-sm mb-6">
                <Link to="/" className="text-gray-500 hover:text-blue-600 transition">Home</Link>
                <span className="text-gray-300">/</span>
                <span className="text-gray-700 font-medium">Informatie</span>
              </nav>

              <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-gradient-to-r from-blue-500/10 to-purple-500/10 text-blue-700 border border-blue-200/50 mb-6">
                📚 WFT Kennisbank
              </span>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                Alles over{' '}
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  WFT examens
                </span>
              </h1>
              
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Van scoringsregels tot studietips, van begrippen tot slagingspercentages. 
                Bereid je optimaal voor op je WFT certificering.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/app"
                  className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:shadow-lg transform hover:scale-105 transition-all"
                >
                  Start met oefenen
                </Link>
                <a
                  href="#categories"
                  className="px-6 py-3 bg-white/70 backdrop-blur-sm text-gray-700 border border-white/50 rounded-xl font-semibold hover:bg-white hover:shadow-md transition-all"
                >
                  Bekijk categorieën ↓
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Categories Section */}
        <section id="categories" className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Categorieën</h2>
            <span className="text-sm text-gray-500">Kies een onderwerp</span>
          </div>
          
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center animate-pulse">
                <span className="text-white font-bold text-2xl">W</span>
              </div>
            </div>
          )}

          {error && (
            <div className="text-center py-12 bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50">
              <p className="text-red-500 mb-4">{error}</p>
              <button 
                onClick={loadData} 
                className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:shadow-lg transition-all"
              >
                Opnieuw proberen
              </button>
            </div>
          )}

          {!loading && !error && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/informatie/${cat.slug}`}
                  className="group bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 overflow-hidden hover:shadow-xl hover:bg-white/90 transition-all duration-300 transform hover:scale-[1.02]"
                >
                  {/* Icon Header with Gradient */}
                  <div className={`h-24 bg-gradient-to-br ${CategoryGradients[cat.slug] || 'from-blue-500 to-indigo-600'} flex items-center justify-center relative overflow-hidden`}>
                    {/* Decorative circles */}
                    <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                    <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />
                    <div className="text-white transform group-hover:scale-110 transition-transform relative z-10">
                      {CategoryIcons[cat.slug] || CategoryIcons.examens}
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-5">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                      {cat.name}
                    </h3>
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                      {cat.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-blue-600 group-hover:translate-x-1 transition-transform flex items-center gap-1">
                        Bekijk 
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* Recent Articles Section */}
        {recentArticles.length > 0 && (
          <section className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Recente artikelen</h2>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                ✨ Nieuwste content
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {recentArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/informatie/${article.category}/${article.slug}`}
                  className="group bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 overflow-hidden hover:shadow-xl hover:bg-white/90 transition-all duration-300 transform hover:scale-[1.02]"
                >
                  <div className="p-6">
                    {/* Category & Read Time */}
                    <div className="flex items-center justify-between mb-3">
                      <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-lg text-xs font-medium bg-gradient-to-r ${CategoryGradients[article.category] || 'from-blue-500 to-indigo-600'} text-white`}>
                        <span className="w-3.5 h-3.5">
                          {CategoryIcons[article.category]}
                        </span>
                        {article.category}
                      </span>
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {article.readTimeMinutes} min
                      </span>
                    </div>

                    {/* Title */}
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors line-clamp-2">
                      {article.title || article.h1}
                    </h3>

                    {/* Description */}
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                      {truncateText(article.metaDescription || article.intro, 100)}
                    </p>

                    {/* Footer */}
                    <div className="flex items-center justify-end pt-4 border-t border-gray-100">
                      <span className="text-sm font-medium text-blue-600 group-hover:translate-x-1 transition-transform flex items-center gap-1">
                        Lees meer
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* CTA Section */}
        <section className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-3xl p-8 sm:p-12 text-center shadow-2xl relative overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />
            
            <div className="relative z-10">
              <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-white/20 text-white mb-6">
                🚀 Klaar om te starten?
              </span>
              
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
                Lezen is goed, oefenen is beter
              </h2>
              <p className="text-blue-100 mb-8 max-w-lg mx-auto text-lg">
                Start vandaag nog met oefenvragen en verhoog je slagingskans voor je WFT examen.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/app"
                  className="px-8 py-4 bg-white text-blue-600 rounded-xl font-semibold hover:bg-blue-50 hover:shadow-lg transform hover:scale-105 transition-all"
                >
                  Start gratis oefenen
                </Link>
                <Link
                  to="/pricing"
                  className="px-8 py-4 bg-white/10 text-white border border-white/30 rounded-xl font-semibold hover:bg-white/20 transition-all"
                >
                  Bekijk prijzen
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="relative z-10 border-t border-white/30 bg-white/60 backdrop-blur-sm mt-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {/* Brand */}
              <div className="col-span-1 md:col-span-2">
                <Link to="/" className="flex items-center space-x-2 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-xl">W</span>
                  </div>
                  <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                    WFT Study Buddy
                  </span>
                </Link>
                <p className="text-gray-600 text-sm max-w-md">
                  Bereid je optimaal voor op je WFT examen met AI-gegenereerde oefenvragen en complete samenvattingen.
                </p>
              </div>

              {/* Links */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-4">Navigatie</h4>
                <nav className="space-y-2">
                  <Link to="/informatie" className="block text-sm text-gray-600 hover:text-blue-600 transition">Informatie</Link>
                  <Link to="/app" className="block text-sm text-gray-600 hover:text-blue-600 transition">Oefenen</Link>
                  <Link to="/#pricing" className="block text-sm text-gray-600 hover:text-blue-600 transition">Prijzen</Link>
                </nav>
              </div>

              {/* Legal */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-4">Juridisch</h4>
                <nav className="space-y-2">
                  <Link to="/privacy" className="block text-sm text-gray-600 hover:text-blue-600 transition">Privacy</Link>
                  <Link to="/terms" className="block text-sm text-gray-600 hover:text-blue-600 transition">Voorwaarden</Link>
                </nav>
              </div>
            </div>

            <div className="border-t border-gray-200 mt-8 pt-8 text-center text-sm text-gray-500" suppressHydrationWarning>
              © {new Date().getFullYear()} WFT Study Buddy. Alle rechten voorbehouden.
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default InformatieOverview;